import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IonPage, isPlatform, useIonViewDidEnter, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  AppHeader_v2 as AppHeader,
  Button_v2 as Button,
  Container_v2 as Container,
  Loading,
  Picture,
  Spinner_v2 as Spinner,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import {
  getAllSelectedAnswers,
  getOnBoardingKey,
  getOnBoardingName,
  getStepNumber,
} from '../../../../redux/onBoarding/selector';
import useToast from '../../../../context/ToastContext/ToastContext';
import { FunnelTypeEnum, useAnalytics, useAuthFlow } from '../../../../shared';
import { changeUserData, getCustomToken } from '../../../../api/user';
import { updateUserSpecialistOrder } from '../../../../redux/user/reducer';
import { resetSpecialist } from '../../../../redux/specialists/reducer';
import { RouteComponentProps } from 'react-router';
import { PrivateRoutes, PublicRoutes } from '../../../../routes/navigation';
import { deepLink } from '../../../../shared/utils/deepLink';
import {
  experimentVariantSelector,
  flowActualSelectedAnswersSelector,
  onBoardingFlowSelector,
} from '../../../../redux/onBoardingFlow/selector';
import { setCurrentScreenId } from '../../../../redux/onBoardingFlow/reducer';
import { globalVariables } from '../../../../redux/firestoreGlobalVariables/selector';
import { userSelector } from 'src/redux/user/selector';
import { setSplashColor } from 'src/shared/utils/splashColor';
import { parseAttributionParams } from 'src/utils/parseAttributionParams';
import { createCustomTokenForAnonymousUser } from 'src/api/auth/auth';

const OnBoardingRedirect: FC<RouteComponentProps> = ({ history }) => {
  const { t, i18n } = useTranslation('onboarding');
  const { showToast } = useToast();
  const { authUser } = useAuthFlow();
  const dispatch = useDispatch();
  const userUpdated = useRef(0);
  const onBoardingName = useSelector(getOnBoardingName);
  const onBoardingKey = useSelector(getOnBoardingKey);
  const userProfile = useSelector(userSelector);
  const stepNumber = useSelector(getStepNumber);
  const selectedAnswers = useSelector(getAllSelectedAnswers);
  const flowSelectedAnswers = useSelector(flowActualSelectedAnswersSelector);
  const flowData = useSelector(onBoardingFlowSelector);
  const globalVars = useSelector(globalVariables);
  const selectedExperimentVariant = useSelector(experimentVariantSelector);
  const [isPageView, setIsPageView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const { trackPredefinedEvent } = useAnalytics();

  const parseUtmParams = useCallback(() => {
    try {
      const utmParams = localStorage.getItem('utm_params');
      if (utmParams) {
        const parsedUtmParams = JSON.parse(utmParams);
        return parsedUtmParams;
      }
      return {};
    } catch (e) {}
  }, []);

  const canUseDeepLink = useMemo(() => {
    return !flowData?.isWebToWeb && (isPlatform('android') || isPlatform('ios')) && !isPlatform('hybrid');
  }, [flowData?.isWebToWeb]);

  const handleRedirectToApp = useCallback(
    async (customToken: string | null) => {
      sessionStorage.removeItem('onBoardingFlowState');
      if (flowData?.isDefault) {
        history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
        return;
      }
      if (canUseDeepLink) {
        const utmParams = parseUtmParams();
        deepLink({ ...utmParams, token: customToken, lang: i18n.language }, 'auth');
        return;
      }

      history.push(
        `${PublicRoutes.AUTH}?token=${customToken}&isWebToWebAuth=${flowData?.isWebToWeb ?? false}&flowId=${flowData?.id}`
      );
    },
    [i18n.language, isPlatform, history, canUseDeepLink, flowData]
  );

  useIonViewWillEnter(() => {
    setSplashColor('#fffffe');
  });

  useIonViewDidEnter(async () => {
    dispatch(setCurrentScreenId(null));
    setIsPageView(true);
    updateUserAndTriggerEvents();
  }, [
    authUser,
    userProfile,
    selectedAnswers,
    flowSelectedAnswers,
    handleRedirectToApp,
    flowData,
    selectedExperimentVariant,
  ]);

  useIonViewWillLeave(() => {
    setIsPageView(false);
    userUpdated.current = 0;
  });

  useEffect(() => {
    return () => {
      userUpdated.current = 0;
    };
  }, []);

  useEffect(() => {
    updateUserAndTriggerEvents();
  }, [
    authUser,
    userProfile,
    selectedAnswers,
    flowSelectedAnswers,
    handleRedirectToApp,
    flowData,
    selectedExperimentVariant,
  ]);

  useEffect(() => {
    if (!isPageView) return;
  }, [isPageView, stepNumber, onBoardingName]);

  const updateUserAndTriggerEvents = async () => {
    try {
      if (userUpdated.current > 0) {
        return;
      }

      setIsLoading(true);

      if (!authUser?.uid) {
        return;
      }

      userUpdated.current += 1;
      let customToken: string | null = null;
      setToken(null);

      if (authUser.email) {
        const customTokenResponse = await getCustomToken(authUser.uid);
        customToken = customTokenResponse?.token ?? null;
      } else {
        const customTokenResponse = await createCustomTokenForAnonymousUser(authUser.uid);
        customToken = customTokenResponse?.data;
      }

      setToken(customToken ?? null);

      trackPredefinedEvent('FUNNEL_COMPLETED', {
        funnel_id: onBoardingKey ?? flowData?.id ?? '',
        funnel_name: (onBoardingName && onBoardingName !== '' ? onBoardingName : null) ?? flowData?.name ?? '',
        funnel_type:
          onBoardingKey === globalVars?.defaultOnBoardingId?.[i18n.language] || flowData?.isDefault
            ? FunnelTypeEnum.DEFAULT
            : flowData?.isWebToWeb
              ? FunnelTypeEnum.WEB_TO_WEB
              : FunnelTypeEnum.WEB_TO_APP,
      });

      const attributionParams = parseAttributionParams();
      await changeUserData(
        {
          onboarding_key: onBoardingKey ?? flowData?.id ?? '',
          app_lang: i18n.language,
          ...(selectedAnswers && {
            onBoardingAnswers: Object.keys(selectedAnswers).length ? selectedAnswers : flowSelectedAnswers,
          }),
          onboarding_experiment_name: selectedExperimentVariant?.name ? selectedExperimentVariant.name : null,
          specialist_order: null,
          is_viewed_specialist_list: false,
          ...attributionParams,
        },
        authUser.uid
      );

      if (!userProfile?.onboarding_key && !flowData?.isDefault) {
        trackPredefinedEvent('Lead', {
          eventInstanceId: authUser.uid,
          userId: authUser.uid,
        });
      }

      dispatch(updateUserSpecialistOrder(null));
      dispatch(resetSpecialist());

      if (flowData?.isDefault || flowData?.isWebToWeb || !canUseDeepLink) {
        handleRedirectToApp(customToken ?? null);
      }

      setIsLoading(false);
    } catch (e) {
      showToast({ error: e });
      setIsLoading(false);
    }
  };

  const promocodeTimerRun = useMemo(() => {
    if ((!flowData?.promocode_duration_min && !!flowData?.duration_min) || !flowData?.promocode_percent) return false;
    return true;
  }, [flowData?.promocode_duration_min, flowData?.promocode_percent]);

  return (
    <IonPage>
      <AppHeader>{promocodeTimerRun && <div className="h-[80px]" />}</AppHeader>
      <Container>
        {flowData?.isWebToWeb || flowData?.isDefault || !canUseDeepLink ? (
          <Loading isOpen={isLoading} />
        ) : (
          <div className={'flex flex-col items-center gap-10'}>
            <Typography variant={TypographyVariantsEnum.Text24Semi}>{t('onboarding.to.app.title')}</Typography>
            <Picture
              width={335}
              height={314}
              src={{
                webp: `/assets/onboarding/app_${i18n.language}_x2.webp`,
                webp2x: `/assets/onboarding/app_${i18n.language}_x2.webp`,
                src: `/assets/onboarding/app_${i18n.language}_x2.png`,
                src2x: `/assets/onboarding/app_${i18n.language}_x2.png`,
              }}
              srcType={'image/png'}
              alt={'Mindly client app'}
            />
            {isLoading && <Spinner />}
            {!isLoading && (
              <Button fullWidth variant={flowData?.theme} onClick={() => handleRedirectToApp(token)}>
                {t('onboarding.to.app.button')}
              </Button>
            )}
          </div>
        )}
      </Container>
    </IonPage>
  );
};

export default OnBoardingRedirect;
